import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown, ArrowRight, CheckCircle, Star,
  Sparkles, Play
} from 'lucide-react';
import Navbar from '../components/Layout/Navbar';
import Footer from '../components/Layout/Footer';
import AnnouncementBar from '../components/Layout/AnnouncementBar';
import GoogleSignInButton from '../components/Auth/GoogleSignInButton';
import { markFbRouteEntry } from '../lib/funnel';

const STACK_CARDS = [
  {
    eyebrow: 'Stop Scrolling. Start Filtering.',
    headline: 'Find the right ad\nin seconds.',
    body: "You don't need to browse thousands of ads every time you need inspiration. IndiAds lets you filter by exactly what you're looking for — so you get straight to the ads that are relevant to your brief, your industry, and your creative challenge.",
    theme: 'light',
    imageRight: false,
    mediaSrc: 'https://media.indiads.cloud/demo/gif-1.mp4',
  },
  {
    eyebrow: '100% Analyzed by Performance Marketers',
    headline: 'Know exactly why\nevery ad works.',
    body: "Saving an ad is easy. Understanding why it works is the hard part. Every ad in IndiAds is researched and broken down by performance marketers — not AI, not algorithms. Real people who understand Indian consumer psychology and what makes someone stop scrolling and click.",
    theme: 'dark',
    imageRight: true,
    mediaSrc: '/demo/image-3.webp',
  },
  {
    eyebrow: 'Growing Library of Winning Ads',
    headline: '1000+ ads.\n750+ added every month.',
    body: "Currently 1000+ curated winning Meta ads from 75+ Indian D2C brands across 25 industries. Every month 750+ new hand-picked ads are added — so your inspiration never runs dry and you're always seeing what's working right now, not six months ago.",
    theme: 'light',
    imageRight: false,
    mediaSrc: '/demo/image-2.webp',
  },
  {
    eyebrow: 'Daily Trending Picks',
    headline: 'The 3 best ads\nto study today.',
    body: "Every day our team picks the 3 strongest ads in the library right now — the ones with the sharpest hooks, most interesting angles, and most relevant to Indian D2C. No algorithm. Just expert eyes on what's actually worth your attention today.",
    theme: 'dark',
    imageRight: true,
    mediaSrc: '/demo/image-1.webp',
  },
  {
    eyebrow: 'Save & Take Notes',
    headline: 'Never lose a\ngood idea again.',
    body: "When an ad or breakdown sparks an idea, save it instantly and leave a note. When you're briefing your creative team or building your next campaign, everything you need is already waiting for you.",
    theme: 'light',
    imageRight: false,
    mediaSrc: '/demo/image-4.webp',
  },
];

const PLACEHOLDER_COLORS = [
  'linear-gradient(135deg,#fde8d8 0%,#fbc4a0 100%)',
  'linear-gradient(135deg,#1a1a2e 0%,#16213e 100%)',
  'linear-gradient(135deg,#e8f4fd 0%,#bde0f7 100%)',
  'linear-gradient(135deg,#0f0f0f 0%,#1c1c1c 100%)',
  'linear-gradient(135deg,#fdf0e8 0%,#f5d9c0 100%)',
];

function StackSection() {
  return (
    <section className="stack-section">
      <div className="container">
        <div className="stack-cards-wrap">
          {STACK_CARDS.map((card, i) => {
            const textPanel = (
              <div className="stack-card-text">
                <h3 className="stack-card-eyebrow">{card.eyebrow}</h3>
                <p className="stack-card-body">{card.body}</p>
              </div>
            );
            const mediaPanel = (
              <div className="stack-card-media" style={{ background: PLACEHOLDER_COLORS[i] }}>
                {card.mediaSrc && (
                  card.mediaSrc.endsWith('.mp4')
                    ? <video src={card.mediaSrc} autoPlay muted loop playsInline className="stack-media-fill" />
                    : <img src={card.mediaSrc} alt={card.eyebrow} className="stack-media-fill" loading={i === 0 ? 'eager' : 'lazy'} />
                )}
              </div>
            );
            const glassVariant = i % 2 === 0 ? 'stack-card-dark-glass' : 'stack-card-light-glass';
            return (
              <div key={i} className={`stack-card ${glassVariant}`}>
                {card.imageRight ? <>{textPanel}{mediaPanel}</> : <>{mediaPanel}{textPanel}</>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  {
    name: 'Priya Sharma',
    role: 'Performance Marketer',
    avatar: 'PS',
    text: "Honestly this has saved me so much time. Earlier I used to spend hours going through different brand pages on ads library. Now I just open IndiAds and filter what I need in minutes.",
  },
  {
    name: 'Rohan Mehta',
    role: 'Creative Strategist',
    avatar: 'RM',
    text: "The breakdown notes are actually really useful. I used to save ads and then forget why I saved them lol. Now I understand the strategy behind each one and it actually helps when I'm briefing creatives.",
  },
  {
    name: 'Karan Verma',
    role: 'Media Buyer',
    avatar: 'KV',
    text: "Finally something made for Indian ads. Was tired of seeing only western brand examples everywhere. The filters are also good — I can find exactly what type of ad I'm looking for without wasting time.",
  },
];

const META_ITEMS = [
  'Juggling through multiple pages',
  'Basic Filters',
  'Guesswork on performance',
  'Thousands of random ads',
  'No save and comment',
  'No downloads',
  '120-180 minutes of manual research',
  'Ads get deactivated',
  "Time taking and doesn't guarantee performance",
];

const INDI_ITEMS = [
  'All top Indian D2C ads in one place',
  'Advanced Filters — Formats, appeal, hook, angle, industry',
  '100% Real Performance marketer breakdown notes',
  'Daily top 3 picks by experts',
  'Save, comment, and organise',
  'Direct download and share with team',
  'Under 5 minutes',
  'Ads live forever',
  'Quick and data-backed creative for high ROAS',
];


const FAQS = [
  { q: 'What is IndiAds?', a: 'A curated library of 1000+ top performing Meta ads from 75+ Indian D2C brands. Every ad is tagged and broken down by performance marketers so you can learn from them and apply it to your own campaigns to build profitable business through Meta ads.' },
  { q: 'How is this different from Meta Ads Library?', a: 'Meta Ads Library shows you the ad. IndiAds shows you why it worked. Every ad comes with an expert breakdown covering the hook, angle, and appeal — plus advanced filters so you find what you need in minutes instead of hours.' },
  { q: 'Who is it for?', a: 'Performance marketers, creative strategists, and brand owners who run Meta ads and want better creative inspiration without spending hours on manual research.' },
  { q: 'How often are new ads added?', a: '750+ new ads are added every month. Every new ad is researched and broken down before it goes live in the library.' },
  { q: 'Is it really free to start?', a: 'Yes. The free plan gives you access to 20 ads with no credit/debit card required. Upgrade anytime when you want full library access.' },
  { q: 'Can I cancel anytime?', a: 'Yes. You can cancel anytime directly from your account.' },
];

function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <div className="faq-list">
      {FAQS.map((faq, i) => (
        <div key={i} className={`faq-item${open === i ? ' faq-item-open' : ''}`} onClick={() => setOpen(open === i ? null : i)}>
          <div className="faq-question">
            <span>{faq.q}</span>
            <ChevronDown size={18} className="faq-chevron" />
          </div>
          {open === i && <p className="faq-answer">{faq.a}</p>}
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const location = useLocation();
  // /fb is the Facebook-ads variant of this same page — identical except no
  // Google button (Google's account picker is unsupported in Meta's in-app
  // browser). Signup needs to hide its own Google button to match, so mark
  // the session the moment they land here, regardless of which CTA they
  // eventually click through to get there.
  const isFbRoute = location.pathname === '/fb';
  useEffect(() => {
    if (isFbRoute) markFbRouteEntry();
  }, [isFbRoute]);

  // Scroll to section when navigating from another page with a hash (e.g. /#pricing)
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(hash);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      else if (attempts < 10) { setTimeout(() => tryScroll(attempts + 1), 100); }
    };
    tryScroll();
  }, []);

  const heroVideoRef = useRef(null);
  const [videoStarted, setVideoStarted] = useState(false);

  // No autoplay — the video only plays once the visitor taps the play
  // button, which is also the user gesture that lets it start unmuted
  // (browsers block audible autoplay without one). Once started, native
  // controls take over for pause/seek/volume.
  function handlePlayClick() {
    if (!heroVideoRef.current) return;
    heroVideoRef.current.muted = false;
    heroVideoRef.current.play().catch(() => {});
    setVideoStarted(true);
  }

  return (
    <div className="landing">
      <AnnouncementBar />
      <Navbar />

      <main>
      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="hero-badge">
            <Sparkles size={13} />
            <span> India's 1st Meta ads swipe file</span>
          </div>
          <h1 className="hero-heading">
            The only library to <br />
            <span className="gradient-text"> inspire winning ads</span>
          </h1>
          <p className="hero-sub">
            IndiAds shows you what Indian audiences respond to. 1000+ Winning ads are tagged, filtered, and broken down so you can apply the insights instantly
          </p>
          <div className="hero-ctas">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Try For Free Now <ArrowRight size={16} />
            </Link>
            {!isFbRoute && (
              <div className="hero-google-btn-wrap">
                <GoogleSignInButton redirectTo="/library" />
              </div>
            )}
          </div>
          <div className="hero-brand-strip">
            <p className="hero-brand-strip-label">Featuring ads from India's top D2C brands</p>
            <div className="hero-brand-strip-logos">
              {[
                { name: 'OZiva', file: 'oziva.svg' },
                { name: 'Plix', file: 'plix.svg' },
                { name: 'Snitch', file: 'snitch.svg' },
                { name: 'AstroTalk', file: 'astrotalk.svg' },
                { name: 'Kapiva', file: 'kapiva.svg' },
              ].map(({ name, file }) => (
                <div key={name} className="hero-brand-logo-img-wrap">
                  <img
                    src={`/brands/${file}`}
                    alt={name}
                    className={`hero-brand-logo-img${file === 'kapiva.svg' ? ' hero-brand-logo-img-flat' : ''}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="hero-video">
            <video
              ref={heroVideoRef}
              controls={videoStarted}
              playsInline
              preload="metadata"
              poster="/demo/vsl-poster-new.png"
              src="https://media.indiads.cloud/demo/Final%20Draft%201.mp4"
            />
            {!videoStarted && (
              <button className="hero-video-play-btn" onClick={handlePlayClick} aria-label="Play video">
                <Play size={30} fill="currentColor" />
              </button>
            )}
          </div>

        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="value-props-section">
        <div className="container">
          <div className="value-props-statement">
            <h2 className="value-props-big-heading">
              <span className="value-props-line-black">Good Advertiser Copy</span>
              <span className="value-props-line-accent">Great Advertiser Steal</span>
            </h2>
            <p className="value-props-quote">
              The best performing ads follow proven frameworks (formats, hooks, angles, etc.), and that's why they generate high ROAS. IndiAds gives you direct access to that playbook — so every creative decision you make is backed by what's already working in your market.
            </p>
          </div>
        </div>
      </section>

      <StackSection />

      {/* MID-PAGE CTA */}
      <section className="mid-cta-section">
        <div className="container">
          <div className="mid-cta-inner">
            <Link to="/signup" className="btn btn-accent mid-cta-btn">
              Try Now for Free <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* COMPARISON — TWO BOX */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="container">
          <div className="section-label">The Difference</div>
          <h2 className="section-heading">Meta Ads Library vs IndiAds</h2>
          <p className="section-sub">See exactly what you get with IndiAds that Meta Ads Library simply can't offer.</p>
          <div className="before-after-grid">
            <div className="before-card">
              <div className="ba-label ba-label-before">Meta Ads Library</div>
              <ul className="ba-list">
                {META_ITEMS.map(item => (
                  <li key={item} className="ba-item ba-item-before">
                    <span className="ba-icon ba-icon-before">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="after-card">
              <div className="ba-label ba-label-after">IndiAds</div>
              <ul className="ba-list">
                {INDI_ITEMS.map(item => (
                  <li key={item} className="ba-item ba-item-after">
                    <span className="ba-icon ba-icon-after">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO USE */}
      <section className="how-section">
        <div className="container">
          <p className="section-label">How it works</p>
          <h2 className="section-heading">Three steps to your next winning ad</h2>
          <p className="section-sub">From search to brief — IndiAds fits into your existing creative workflow in minutes.</p>
          <div className="how-steps">
            {[
              {
                num: '01',
                title: 'Browse and filter',
                copy: 'Use advanced filters to find ads by hook type, format, industry, or angle — get to the right inspiration in seconds.',
              },
              {
                num: '02',
                title: 'Read the breakdown',
                copy: 'Open any ad and read the expert note explaining exactly why it worked — hook, angle, emotional trigger, all decoded.',
              },
              {
                num: '03',
                title: 'Save and come back',
                copy: "Save ads to your personal collection, drop a note on them, and access everything when you're ready to brief your next campaign.",
              },
            ].map(({ num, title, copy }) => (
              <div key={num} className="how-step">
                <div className="how-step-num">{num}</div>
                <h3 className="how-step-title">{title}</h3>
                <p className="how-step-copy">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section" id="testimonials">
        <div className="container">
          <div className="section-label">Testimonials</div>
          <h2 className="section-heading">Loved by Indian Advertisers</h2>
          <p className="section-sub">Real words from Real users.</p>
          <div className="testimonials-track">
            {TESTIMONIALS.map(({ name, role, text }) => (
              <div key={name} className="testimonial-card">
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#f59e0b" color="#f59e0b" />)}
                </div>
                <p className="testimonial-text">{text}</p>
                <div className="testimonial-author">
                  <div className="testimonial-author-info">
                    <p className="testimonial-name">{name}</p>
                    <p className="testimonial-role">{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="section section-subtle" id="pricing">
        <div className="container">
          <div className="pricing-centered">
            <div className="pricing-early-badge">
              Early access offer — 50% off for first 500 founding members. Spots filling fast.
            </div>
            <div className="pricing-card pricing-card-featured pricing-card-single">
              <div className="pricing-card-header">
                <p className="pricing-plan-name">Monthly</p>
                <div className="pricing-price">
                  <span className="pricing-original">₹999</span>
                  <span className="pricing-currency">₹</span>
                  <span className="pricing-amount">499</span>
                  <span className="pricing-period">/month</span>
                </div>
              </div>
              <ul className="pricing-features pricing-features-lg">
                {[
                  'Full ad library',
                  'Advanced filters',
                  'Expert breakdowns',
                  'Daily trending picks',
                  'Save and comment',
                  'Weekly new ads',
                ].map(f => (
                  <li key={f} className="pricing-feature pricing-feature-featured">
                    <CheckCircle size={16} /> {f}
                  </li>
                ))}
              </ul>
              <div className="pricing-single-cta">
                <Link to="/signup" className="btn btn-accent btn-lg" style={{width:'100%',justifyContent:'center'}}>
                  Try For Free Now
                </Link>
                <p className="pricing-no-card">No card required</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container container-narrow">
          <div className="section-label">FAQ</div>
          <h2 className="section-heading">Common Questions</h2>
          <FAQ />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section cta-section">
        <div className="container container-narrow">
          <div className="cta-box">
            <h2 className="cta-heading">Ready to Run Better Ads?</h2>
            <p className="cta-sub">Join India's first Meta ads swipe file — founding member spots filling fast.
</p>
            <div className="cta-actions">
              <Link to="/signup" className="btn btn-primary cta-btn-hero">
                Try For Free Now
 <ArrowRight size={18} />
              </Link>
              <p className="cta-note">No credit card required</p>
            </div>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}
